import type * as React from 'react';
import type { PropsOf } from '@emotion/react';
import { useTheme as useEmotionTheme, css as emotionCSS } from '@emotion/react';
import type { CreateStyledComponent, FilteringStyledOptions, StyledOptions } from '@emotion/styled/src/types';
import type { ReactJSXIntrinsicElements } from '@emotion/styled/src/jsx-namespace';
import { expectMobileDeps, expectWebDeps, hasMobileDeps, hasWebDeps } from '@epihub/web/src/_ctx/deps';
import type { ThemeDefV0 } from './versions/ThemeDefV0';

export type EmotionThemeType<Theme extends object = object> =
  & Theme
  & {
    v0: ThemeDefV0['Theme'];
    updateTheme: (
      mutate: (
        state: ThemeDefV0['State'],
        getTheme: () => { theme: ThemeDefV0['Theme']; state: ThemeDefV0['State'] },
      ) => void,
    ) => void;
  };

export function createEmotionFunctions<
  // How to create the theme
  Creator extends object = object,
  // The mutable props of the theme
  State extends object = object,
  // The result, when state changes Creator + State produces theme
  Theme extends object = object,
>() {
  interface Epihub_BaseCreateStyled {
    <
      C extends React.ComponentClass<React.ComponentProps<C>>,
      ForwardedProps extends
        & keyof React.ComponentProps<C>
        & string = keyof React.ComponentProps<C> & string,
    >(
      component: C,
      options: FilteringStyledOptions<React.ComponentProps<C>, ForwardedProps>,
    ): CreateStyledComponent<
      Pick<PropsOf<C>, ForwardedProps> & {
        theme?: EmotionThemeType<Theme>;
      },
      {},
      {
        ref?: React.Ref<InstanceType<C>>;
      }
    >;

    <C extends React.ComponentClass<React.ComponentProps<C>>>(
      component: C,
      options?: StyledOptions<React.ComponentProps<C>>,
    ): CreateStyledComponent<
      PropsOf<C> & {
        theme?: EmotionThemeType<Theme>;
      },
      {},
      {
        ref?: React.Ref<InstanceType<C>>;
      }
    >;

    <
      C extends React.ComponentType<React.ComponentProps<C>>,
      ForwardedProps extends
        & keyof React.ComponentProps<C>
        & string = keyof React.ComponentProps<C> & string,
    >(
      component: C,
      options: FilteringStyledOptions<React.ComponentProps<C>, ForwardedProps>,
    ): CreateStyledComponent<
      Pick<PropsOf<C>, ForwardedProps> & {
        theme?: EmotionThemeType<Theme>;
      }
    >;

    <C extends React.ComponentType<React.ComponentProps<C>>>(
      component: C,
      options?: StyledOptions<React.ComponentProps<C>>,
    ): CreateStyledComponent<
      PropsOf<C> & {
        theme?: EmotionThemeType<Theme>;
      }
    >;

    <
      Tag extends keyof ReactJSXIntrinsicElements,
      ForwardedProps extends
        & keyof ReactJSXIntrinsicElements[Tag]
        & string = keyof ReactJSXIntrinsicElements[Tag] & string,
    >(
      tag: Tag,
      options: FilteringStyledOptions<
        ReactJSXIntrinsicElements[Tag],
        ForwardedProps
      >,
    ): CreateStyledComponent<
      { theme?: EmotionThemeType<Theme>; as?: React.ElementType },
      Pick<ReactJSXIntrinsicElements[Tag], ForwardedProps>
    >;

    <Tag extends keyof ReactJSXIntrinsicElements>(
      tag: Tag,
      options?: StyledOptions<ReactJSXIntrinsicElements[Tag]>,
    ): CreateStyledComponent<
      { theme?: EmotionThemeType<Theme>; as?: React.ElementType },
      ReactJSXIntrinsicElements[Tag]
    >;
  }

  type Epihub_StyledTags = {
    [Tag in keyof ReactJSXIntrinsicElements]: CreateStyledComponent<
      {
        theme?: EmotionThemeType<Theme>;
        as?: React.ElementType;
      },
      ReactJSXIntrinsicElements[Tag]
    >;
  };

  interface Epihub_CreateStyled extends Epihub_BaseCreateStyled, Epihub_StyledTags {}

  function useTheme() {
    const anyTheme = useEmotionTheme();
    return anyTheme as EmotionThemeType<Theme>;
  }

  const styled = (() => {
    if (hasWebDeps()) {
      return expectWebDeps().emotion;
    }
    if (hasMobileDeps()) {
      return expectMobileDeps().emotion;
    }

    throw new Error('deps not set, call setDeps in _init');
  })();

  return {
    styled: styled as Epihub_CreateStyled,
    useTheme,
    css: emotionCSS,
  };
}
